import { styled } from '@linaria/react';
import { Radio } from '@mantine/core';
import { useId } from 'react';
import { Badge } from '../../../../../ui-library/badge/badge';
import { Icon } from '../../../../../ui-library/icon/icon';
import type { RadioCardOption } from './settings.type';

interface RadioCardGroupProps<TValue extends string> {
  /** The selected option's value. */
  value: TValue;
  onChange: (value: TValue) => void;
  options: RadioCardOption<TValue>[];
  /**
   * The group's accessible name, usually the card heading's text. Mantine only
   * names its radio group through its own label, so it is rendered visually
   * hidden next to the visible heading.
   */
  label: string;
  disabled?: boolean;
}

/**
 * Renders a single-choice group as side-by-side cards, each with a radio
 * indicator, label, and one-line description. Built on Mantine `Radio.Card`, so
 * arrow keys move between options and the group is announced as a radio group.
 * The group is one Tab stop, and each card is named by its label and described
 * by its description.
 *
 * @param {RadioCardGroupProps<TValue>} props - The options, current value, and change handler
 * @returns {JSX.Element} The radio-card group
 *
 * @example
 * <RadioCardGroup
 *   label="Default approval mode"
 *   value={mode}
 *   onChange={setMode}
 *   options={[{ value: 'ask', label: 'Ask', description: 'Flora waits for your OK.' }]}
 * />
 */
export function RadioCardGroup<TValue extends string>({
  value,
  onChange,
  options,
  label,
  disabled = false,
}: RadioCardGroupProps<TValue>) {
  const idPrefix = useId();
  // One Tab stop per group (the checked card, or the first when none is); arrow keys move within it.
  const tabStopValue = options.some((option) => option.value === value) ? value : options[0]?.value;

  return (
    <Group
      value={value}
      // Mantine also reports a click on the already-checked card, which isn't a change.
      onChange={(next) => {
        if (next !== value) onChange(next as TValue);
      }}
      label={label}
    >
      <Options>
        {options.map((option) => {
          const labelId = `${idPrefix}-${option.value}-label`;
          const descriptionId = `${idPrefix}-${option.value}-description`;

          return (
            <Card
              key={option.value}
              value={option.value}
              disabled={disabled}
              radius="md"
              data-selected={option.value === value || undefined}
              tabIndex={option.value === tabStopValue ? 0 : -1}
              aria-labelledby={labelId}
              aria-describedby={descriptionId}
            >
              <CardBody>
                <Radio.Indicator color="var(--secondary-base)" />
                <CardText>
                  <CardLabel id={labelId}>
                    {option.icon && (
                      <IconSlot aria-hidden="true">
                        <Icon name={option.icon} size={18} color="var(--ink-dark)" />
                      </IconSlot>
                    )}
                    {option.label}
                    {option.badge && <Badge tone="success">{option.badge}</Badge>}
                  </CardLabel>
                  <CardDescription id={descriptionId}>{option.description}</CardDescription>
                </CardText>
              </CardBody>
            </Card>
          );
        })}
      </Options>
    </Group>
  );
}

// Keeps Mantine's group label (the radiogroup's accessible name) for screen
// readers only; the card heading is the visible label.
const Group = styled(Radio.Group)`
  & .mantine-RadioGroup-label {
    position: absolute;
    width: 1px;
    height: 1px;
    margin: -1px;
    padding: 0;
    overflow: hidden;
    clip: rect(0 0 0 0);
    white-space: nowrap;
    border: 0;
  }
`;

const Options = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 12px;
`;

const Card = styled(Radio.Card)`
  flex: 1 1 240px;
  min-width: 0;
  padding: 16px;
  border: 1px solid var(--sky-base);
  background: var(--base-white);

  &[data-selected] {
    border-color: var(--secondary-base);
    box-shadow: inset 0 0 0 1px var(--secondary-base);
    background: var(--secondary-lightest);
  }

  &:focus-visible {
    outline: 2px solid var(--secondary-base);
    outline-offset: 2px;
  }
`;

const CardBody = styled.div`
  display: flex;
  align-items: flex-start;
  gap: 12px;
`;

const CardText = styled.div`
  min-width: 0;
`;

const CardLabel = styled.div`
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  gap: 6px;
  font-size: var(--font-size-regular);
  font-weight: var(--font-weight-semibold);
  line-height: 1.25rem;
  color: var(--ink-darkest);
`;

const CardDescription = styled.div`
  margin-top: 4px;
  font-size: var(--font-size-small);
  line-height: 1.125rem;
  color: var(--ink-base);
`;

const IconSlot = styled.span`
  display: inline-flex;
`;

export type { RadioCardGroupProps };
